<script setup lang="ts">
import { onMounted, shallowRef, watch } from 'vue';
import DashboardPanel from '@/modules/dashboard/components/DashboardPanel.vue';
import MarketplaceDepthOfBook from '@/modules/marketplace/components/MarketplaceDepthOfBook.vue';
import SimpleSecuritySearch from '@/modules/manual-loan/components/SimpleSecuritySearch.vue';
import { Security } from '@/modules/common/models';
import { rateTypeOptions } from '@/modules/marketplace/helpers/marketplace';
import { useRateTypeStore } from '@/store/rate-type-store';
import { useManualLoansApiService } from '@/modules/manual-loan/services/manual-loans-api.service';
import { store as vuex } from '@/store/store';
import { useRoute, useRouter } from '@/router/router';
import { storeToRefs } from 'pinia';

const router = useRouter();
const route = useRoute();
const manualLoansApi = useManualLoansApiService();
const { marketplaceRateType: rateType } = storeToRefs(useRateTypeStore());

const selectedSecurity = shallowRef<Security | null>(null);

watch(
  () => route.params.cusip,
  (newCusip) => {
    // fix until we move from "hash" router to "history" router
    // in case user manually edits URL
    if (newCusip === undefined) {
      window.location.reload();
      return;
    }
    void fetch();
  }
);

function handleSecurityChange(security: Security | null): void {
  // Only change the page if user has selected a new security
  // (i.e. do nothing if user unselects security)
  if (security) {
    void router.push({
      name: 'broker-user.client-monitor.marketplace-depth-of-book',
      params: { cusip: security.cusip },
    });
  }
}

async function fetchSecurity(): Promise<void> {
  if (selectedSecurity.value === null || selectedSecurity.value.cusip !== route.params.cusip) {
    selectedSecurity.value = await manualLoansApi.fetchSecurityByCusip(route.params.cusip);
  }
}

async function fetch(): Promise<void> {
  await fetchSecurity();
  vuex.commit('updateLastVisitedSymbolOverview', selectedSecurity.value as Security);
}

onMounted(() => {
  void fetch();
});
</script>

<template>
  <div class="wrapper d-flex flex-column fill-height">
    <div class="gap-1 d-flex align-center">
      <SimpleSecuritySearch
        class="security ml-2 my-2"
        clear-after-select
        label="Security"
        :value="selectedSecurity"
        @change="handleSecurityChange"
      />
      <VSelect
        v-model="rateType"
        class="ma-2 ml-auto flex-grow-0 flex-basis-25"
        :items="rateTypeOptions"
        label="Rate Type"
      />
    </div>

    <div class="d-flex flex-column flex">
      <DashboardPanel title="Depth of Book">
        <MarketplaceDepthOfBook as-broker :cusip="route.params.cusip" />
      </DashboardPanel>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.wrapper {
  width: 100%;
}

.security {
  max-width: 300px;
}

.gap-1 {
  gap: 1rem;
}
</style>
